import React, { useState } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { useTranslation } from '../i18n';
const GOOGLE_REVIEWS_URL = 'https://www.google.com/maps/place/Dentico+Clinic+-+Private+Dentist+in+Angel,+Islington/@51.5308911,-0.1029003,18z/data=!4m8!3m7!1s0x48760f7c6534e077:0xa54a590bd3b2a149!8m2!3d51.5308911!4d-0.1029003!9m1!1b1!16s%2Fg%2F11vq9ndlvk?entry=ttu&g_ep=EgoyMDI2MDIwMS4wIKXMDSoASAFQAw%3D%3D';
const LEAVE_REVIEW_URL = 'https://g.page/r/CUmhstMLWUqlEBM/review';
export function GoogleReviews() {
  const {
    t
  } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  // 5-star reviews data (all are 5-star filtered)
  const allReviews = [{
    name: t('reviews.review1.name'),
    text: t('reviews.review1.text'),
    date: t('reviews.review1.date'),
    rating: 5
  }, {
    name: t('reviews.review2.name'),
    text: t('reviews.review2.text'),
    date: t('reviews.review2.date'),
    rating: 5
  }, {
    name: t('reviews.review3.name'),
    text: t('reviews.review3.text'),
    date: t('reviews.review3.date'),
    rating: 5
  }, {
    name: t('reviews.review4.name'),
    text: t('reviews.review4.text'),
    date: t('reviews.review4.date'),
    rating: 5
  }, {
    name: t('reviews.review5.name'),
    text: t('reviews.review5.text'),
    date: t('reviews.review5.date'),
    rating: 5
  }, {
    name: t('reviews.review6.name'),
    text: t('reviews.review6.text'),
    date: t('reviews.review6.date'),
    rating: 5
  }];
  // Show max 6 reviews, with option to load more
  const displayedReviews = showAll ? allReviews : allReviews.slice(0, 6);
  // Get reviewer initials (first name + last initial)
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0]} ${parts[1].charAt(0)}.`;
    }
    return name;
  };
  if (allReviews.length === 0) {
    return null; // Hide section if no reviews
  }
  return <section className="py-16 bg-gray-50" data-id="element-288">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-289">
        {/* Header */}
        <div className="text-center mb-8" data-id="element-290">
          <div className="flex items-center justify-center gap-2 mb-4" data-id="element-291">
            <div className="flex gap-1" data-id="element-292">
              {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-6 h-6 text-yellow-400 fill-current" data-id="element-293" />)}
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-294">
            {t('reviews.title')}
          </h2>
          <p className="text-lg text-gray-600 font-medium mb-2" data-id="element-295">
            {t('reviews.subtitle')}
          </p>
          {/* Disclaimer */}
          <p className="text-sm text-gray-500" data-id="element-296">{t('reviews.disclaimer')}</p>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10" data-id="element-297">
          {displayedReviews.map((review, idx) => <Card key={idx} className="p-6 h-full flex flex-col" data-id="element-298">
              {/* 5 Stars */}
              <div className="flex items-center gap-1 mb-4" data-id="element-299">
                {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" data-id="element-300" />)}
              </div>

              {/* Review Text */}
              <p className="text-gray-700 mb-6 flex-grow leading-relaxed" data-id="element-301">
                "{review.text}"
              </p>

              {/* Reviewer Info */}
              <div className="flex justify-between items-end mt-auto pt-4 border-t border-gray-100" data-id="element-302">
                <span className="font-semibold text-gray-900" data-id="element-303">
                  {getInitials(review.name)}
                </span>
                <span className="text-sm text-gray-400" data-id="element-304">{review.date}</span>
              </div>
            </Card>)}
        </div>

        {/* Load More Button (if more than 6 reviews) */}
        {allReviews.length > 6 && !showAll && <div className="text-center mb-8" data-id="element-305">
            <Button variant="outline" onClick={() => setShowAll(true)} data-id="element-306">
              Load more reviews
            </Button>
          </div>}

        {/* CTA Buttons */}
        <div className="text-center flex flex-col sm:flex-row gap-4 justify-center" data-id="element-307">
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-id="element-308">
            <Button variant="outline" size="lg" className="gap-2" data-id="element-309">
              <ExternalLink className="w-5 h-5" data-id="element-310" />
              {t('reviews.viewAllButton')}
            </Button>
          </a>
          <a href={LEAVE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-id="element-311">
            <Button variant="primary" size="lg" className="gap-2" data-id="element-312">
              <Star className="w-5 h-5" data-id="element-313" />
              {t('reviews.leaveReview')}
            </Button>
          </a>
        </div>
      </div>
    </section>;
}